import { forwardRef, useCallback, useContext, useEffect, useRef, useState } from 'react';
import type { ChangeEvent, FC, PropsWithoutRef, Ref } from 'react';
import styled from 'styled-components';

import ComboBox from '../ComboBox';
import type { MenuItemProps, MenuProps } from '../Menu';
import { useI18n, useTestIds, useUID } from '../../hooks';
import { createUID, withTestIds, createClassName } from '../../utils';
import FormField from '../FormField';
import type { FormControlProps } from '../FormControl';
import type { BaseProps, ForwardProps, TestIdProp } from '../../types';
import Flex from '../Flex';
import { defaultThemeProp } from '../../theme';

import PoweredByGoogleImage from './PoweredByGoogleImage';
import CurrentLocationButton from './CurrentLocationButton';
import MapsContext from './MapsContext';
import { getNavigatorPosition, isValueACoordinate, loadMapsAPI } from './utils';
import type { Bias, LatLng } from './Location.types';
import LocationView from './LocationView';
import { StyledLocationView } from './LocationView.styles';
import type { LocationViewProps } from './LocationView';
import { getLocationInputTestIds } from './Location.test-ids';

export interface LocationInputProps extends BaseProps, FormControlProps, TestIdProp {
  /**
   * Get user current location on component first render.
   * @default false
   */
  defaultToCurrentLocation?: boolean;
  /** The value of the location field. */
  value?: string;
  /**
   * Callback fired on every change of the location input.
   * The argument passed back is the component's value prop.
   */
  onChange?: (value: string) => void;
  /**
   * Callback fired when the control's input loses focus.
   * The argument passed back is the component's value prop.
   */
  onBlur?: (value: string) => void;
  /** Callback fired when user chooses location from the dropdown of suggestions or submits input value. */
  onSelect?: (value: {
    name?: string;
    address?: string;
    latitude?: number;
    longitude?: number;
  }) => void;
  /**
   * Only select coordinates on map clicks.
   * @default false
   */
  onlyCoordinates?: boolean;
  /** Biasing query results towards user location/preference. */
  bias?: Bias;
  /**
   * Set to true renders the map view below input.
   * @default false
   */
  map?: {
    /** Location coordinates used for the map view. */
    location?: LatLng;
    height: LocationViewProps['height'];
    zoomLevel?: LocationViewProps['zoomLevel'];
    onClick?: LocationViewProps['onClick'];
  };
  /** Callback fired when an error occurs. This function gets called with one argument of type Error. */
  onError?: (error: Error) => void;
}

const StyledLocationInput = styled(ComboBox)`
  z-index: 1;
`;

StyledLocationInput.defaultProps = defaultThemeProp;

const StyledLocationMap = styled.div`
  ${StyledLocationView} {
    border-top-left-radius: 0;
    border-top-right-radius: 0;
  }
`;

StyledLocationMap.defaultProps = defaultThemeProp;

const LocationInput: FC<LocationInputProps & ForwardProps> = forwardRef(function LocationInput(
  props: PropsWithoutRef<LocationInputProps>,
  ref: Ref<HTMLInputElement>
) {
  const uid = useUID();
  const {
    testId,
    label,
    labelHidden,
    id = uid,
    value = '',
    info,
    status,
    required,
    readOnly,
    disabled,
    additionalInfo,
    onSelect,
    onError,
    onChange,
    onBlur,
    bias,
    defaultToCurrentLocation,
    onlyCoordinates = false,
    map,
    className,
    ...restProps
  } = props;

  const testIds = useTestIds(testId, getLocationInputTestIds);

  const { name, ...providerOpts } = useContext(MapsContext);
  const t = useI18n();
  const [placePredictionMenuItems, setPlacePredictionMenuItems] = useState<MenuProps['items']>([]);
  const [filterValue, setFilterValue] = useState('');
  const [debouncedFilterValue, setDebouncedFilterValue] = useState('');
  const [mapLoading, setMapLoading] = useState(false);
  const sessionTokenRef = useRef<google.maps.places.AutocompleteSessionToken | undefined>();

  const getSessionToken = useCallback(() => sessionTokenRef.current, []);

  useEffect(() => {
    (async () => {
      if (defaultToCurrentLocation) {
        setMapLoading(true);
        const mapAPIObj = await loadMapsAPI(name, providerOpts);

        const navigatorPosition = await getNavigatorPosition();
        const currentLocationAddress = await mapAPIObj.getAddress(navigatorPosition);
        setMapLoading(false);
        onSelect?.(currentLocationAddress);
      }
    })();
  }, []);

  useEffect(() => {
    if (!filterValue) {
      setDebouncedFilterValue('');
      return;
    }
    const timer = window.setTimeout(() => setDebouncedFilterValue(filterValue), 500);
    return () => window.clearTimeout(timer);
  }, [filterValue]);

  useEffect(() => {
    (async () => {
      try {
        const mapAPIObj = await loadMapsAPI(name, providerOpts);

        if (debouncedFilterValue) {
          if (onlyCoordinates) {
            sessionTokenRef.current = undefined;
            const { latitude, longitude } = await mapAPIObj.getCoords(debouncedFilterValue);
            setPlacePredictionMenuItems([
              {
                primary: t('exact_location'),
                secondary: [`${latitude}, ${longitude}`],
                id: createUID(),
                onClick: () => {
                  setFilterValue('');
                  onSelect?.({ latitude, longitude });
                }
              }
            ]);
          } else {
            if (isValueACoordinate(debouncedFilterValue)) {
              sessionTokenRef.current = undefined;
              const { latitude, longitude } = await mapAPIObj.getCoords(debouncedFilterValue);
              const { name: addressName, address } = await mapAPIObj.getAddress({
                latitude,
                longitude
              });
              const menuItems = [
                {
                  primary: t('exact_location'),
                  secondary: [`${latitude}, ${longitude}`],
                  id: createUID(),
                  onClick: () => {
                    setFilterValue('');
                    onSelect?.({
                      latitude,
                      longitude
                    });
                  }
                },
                {
                  primary: addressName,
                  secondary: [address],
                  id: createUID(),
                  onClick: () => {
                    setFilterValue('');
                    onSelect?.({ name: addressName, address, latitude, longitude });
                  }
                }
              ];

              setPlacePredictionMenuItems(menuItems);
              return;
            }
            const { placeSuggestions, token } = await mapAPIObj.getPlacePredictions(
              debouncedFilterValue,
              bias,
              sessionTokenRef.current
            );
            sessionTokenRef.current = token;
            if (placeSuggestions.length === 1) {
              const exactLocation = await mapAPIObj.getPlaceById(
                placeSuggestions[0].placeId,
                token
              );
              sessionTokenRef.current = undefined;
              const menuItems = placeSuggestions.map(item => ({
                id: item.placeId,
                primary: item.mainText,
                secondary: [item.secondaryText],
                onClick: () => {
                  setFilterValue('');
                  onSelect?.(exactLocation);
                }
              }));
              if (exactLocation) {
                menuItems.push({
                  primary: t('exact_location'),
                  secondary: [`${exactLocation.latitude}, ${exactLocation.longitude}`],
                  id: createUID(),
                  onClick: () => {
                    setFilterValue('');
                    onSelect?.({
                      latitude: exactLocation.latitude,
                      longitude: exactLocation.longitude
                    });
                  }
                });
              }
              setPlacePredictionMenuItems(menuItems);
            } else {
              setPlacePredictionMenuItems(
                placeSuggestions.map(item => ({
                  id: item.placeId,
                  primary: item.mainText,
                  secondary: [item.secondaryText],
                  onClick: (menuItemId: MenuItemProps['id']) => {
                    mapAPIObj.getPlaceById(menuItemId, token).then(menuItemLocation => {
                      sessionTokenRef.current = undefined;
                      setFilterValue('');
                      onSelect?.(menuItemLocation);
                    });
                  }
                }))
              );
            }
          }
        } else {
          setPlacePredictionMenuItems([]);
          sessionTokenRef.current = undefined;
        }
      } catch (e: any) {
        setPlacePredictionMenuItems([]);
        onError?.(e);
      }
    })();
  }, [debouncedFilterValue, bias, onError, onSelect, onlyCoordinates]);

  const onInputChange = useCallback(
    (e: ChangeEvent<HTMLInputElement>) => {
      setFilterValue(e.target.value.trim());
      onChange?.(e.target.value);
    },
    [onChange]
  );

  const onMapClick = useCallback(
    (placeInfo: { latitude: number; longitude: number; name?: string; address?: string }) => {
      if (readOnly) return;

      sessionTokenRef.current = undefined;

      if (onlyCoordinates) {
        const coords = {
          latitude: placeInfo.latitude,
          longitude: placeInfo.longitude
        };
        onSelect?.(coords);
        map?.onClick?.(coords);
      } else {
        onSelect?.(placeInfo);
        map?.onClick?.(placeInfo);
      }
    },
    [readOnly, onSelect, map?.onClick, onlyCoordinates]
  );

  const Comp = (
    <>
      <StyledLocationInput
        id={id}
        info={info}
        data-testid={testIds.control}
        placeholder={t('location_input_placeholder')}
        menu={
          filterValue
            ? {
                items: placePredictionMenuItems.concat([
                  {
                    id: createUID(),
                    primary: t('use_input_value', [filterValue]),
                    onClick: () => {
                      setFilterValue('');
                      onSelect?.({ name: filterValue });
                    }
                  }
                ]),
                emptyText: t('location_not_found_text'),
                mode: 'action',
                footer: !map && (
                  <Flex container={{ justify: 'end' }}>
                    <PoweredByGoogleImage />
                  </Flex>
                )
              }
            : undefined
        }
        actions={
          !disabled &&
          !readOnly && (
            <CurrentLocationButton
              onError={onError}
              onResolve={resolved => {
                if (onlyCoordinates) {
                  onSelect?.({ latitude: resolved.latitude, longitude: resolved.longitude });
                } else {
                  onSelect?.(resolved);
                }
              }}
            />
          )
        }
        onChange={onInputChange}
        onBlur={() => {
          setFilterValue('');
          onBlur?.(value);
        }}
        value={value}
        required={required}
        disabled={disabled}
        readOnly={readOnly}
        status={status}
        {...restProps}
        className={createClassName('location-input', className)}
      />
      {map && (
        <StyledLocationMap>
          <LocationView
            {...map}
            pins={map.location ? [{ ...map.location }] : []}
            onClick={onMapClick}
            onError={onError}
            disabled={disabled}
            loading={mapLoading}
            centerMapOnChange
            getSessionToken={getSessionToken}
          />
        </StyledLocationMap>
      )}
    </>
  );

  return label ? (
    <FormField
      testId={testIds}
      label={label}
      labelHidden={labelHidden}
      id={id}
      info={info}
      readOnly={readOnly}
      status={status}
      ref={ref}
      required={required}
      disabled={disabled}
      additionalInfo={additionalInfo}
    >
      {Comp}
    </FormField>
  ) : (
    Comp
  );
});

export default withTestIds(LocationInput, getLocationInputTestIds);
